import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthProvider";
import { FavoritesContext, SearchContext } from "../../context";
import { MessageContext } from "../../context/MessageContext";
import { EditorContext } from "../../editor/context/EditorContext/EditorProvider";
import useModal from "../../hooks/useModal";
import DownloadModal from "../DownloadModal";
import AddToPlaylistModal from "../AddToPlaylistModal";
import {
  INTENTS,
  readIntent,
  stripIntent
} from "../../utils/postLoginRedirect";

/**
 * resumes an action a guest was gated on (download, add to playlist, favorite,
 * open in editor) once they land back from signin/signup
 * - adds `?intent=<type>&intentTrackId=<id>` to the url params so the action
 *   persists through login/signup
 */
const PendingIntentHandler = () => {
  const auth = useContext(AuthContext);
  const search = useContext(SearchContext);
  const fav = useContext(FavoritesContext);
  const editor = useContext(EditorContext);
  const { showMessage } = useContext(MessageContext);
  const history = useHistory();
  const location = useLocation();

  const downloadModal = useModal();
  const playlistModal = useModal();
  const [playlistTrack, setPlaylistTrack] = useState(null);

  const intent = readIntent(location.search);

  const resume = async ({ type, trackId }) => {
    const track = await search.fetchTrack(trackId);
    if (!track) return;

    if (type === INTENTS.DOWNLOAD) {
      await search.setDownloadTrack(track);
      downloadModal.open();
    } else if (type === INTENTS.PLAYLIST) {
      setPlaylistTrack(track);
      playlistModal.open();
    } else if (type === INTENTS.EDITOR) {
      // same guard as a normal "Open in Editor" click (useEditorAccess)
      if (track.editorOpenable && track.stem?.length > 0) {
        editor.requestTrackOpen(track, true);
      }
    } else if (type === INTENTS.FAVORITE) {
      if (!track.favorite) {
        const res = await fav.addToFavorites(track.id);

        if (!res || res.errors) return;
        fav.addTrackToFavoritelist(track);
      }
      showMessage({
        type: "success",
        duration: 3000,
        title: "Added to favorites",
        description: `${track.title} was saved to your favorites`
      });
    }
  };

  useEffect(() => {
    if (!intent) return;
    // strip first so a reload or back-nav can't replay the action
    history.replace(stripIntent(location), location.state);

    // a guest can only hit this via a pasted link; there's nothing to resume
    if (auth.user.role !== "private") return;

    resume(intent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent?.type, intent?.trackId]);

  return (
    <>
      {downloadModal.visible && <DownloadModal modalState={downloadModal} />}
      {playlistModal.visible && playlistTrack && (
        <AddToPlaylistModal modalState={playlistModal} track={playlistTrack} />
      )}
    </>
  );
};

export default PendingIntentHandler;
